import { PageWrapper } from "@/components";
import * as GlobalS from "@/components/styled";
import {
  useGetBackdatingSettingsQuery,
  useSaveBackdatingSettingsMutation,
} from "@/services";
import { Button, Form, InputNumber, Space, Typography } from "antd";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";

const MIN_BACKDATE_DAYS = 0;
const MAX_BACKDATE_DAYS = 999;

const { Paragraph } = Typography;

const AdminConfigPage = () => {
  const intl = useIntl();
  const [form] = Form.useForm();

  const {
    data: settings,
    isFetching,
    isLoading: isInitialLoad,
  } = useGetBackdatingSettingsQuery();
  const [saveBackdatingSettings, { isLoading: isSaving }] =
    useSaveBackdatingSettingsMutation();

  const currentValue = settings?.maxBackdateDays;

  const watchedValue = Form.useWatch("maxBackdateDays", form);
  const normalizedWatchedValue =
    typeof watchedValue === "number"
      ? watchedValue
      : watchedValue !== undefined && watchedValue !== null
      ? Number(watchedValue)
      : undefined;
  const hasChanges =
    normalizedWatchedValue !== undefined &&
    normalizedWatchedValue !== currentValue;

  const validateBackdateWindow = (_, value) => {
    if (value === undefined || value === null || value === "") {
      return Promise.reject(
        intl.formatMessage({ id: "adminConfig.backdating.validation.required" })
      );
    }

    if (Number.isNaN(value)) {
      return Promise.reject(
        intl.formatMessage({ id: "adminConfig.backdating.validation.required" })
      );
    }

    if (value < MIN_BACKDATE_DAYS || value > MAX_BACKDATE_DAYS) {
      return Promise.reject(
        intl.formatMessage(
          { id: "adminConfig.backdating.validation.range" },
          { min: MIN_BACKDATE_DAYS, max: MAX_BACKDATE_DAYS }
        )
      );
    }

    return Promise.resolve();
  };

  const handleReset = () => {
    if (currentValue !== undefined) {
      form.setFieldsValue({ maxBackdateDays: currentValue });
    } else {
      form.resetFields();
    }
  };

  const handleSubmit = ({ maxBackdateDays }) => {
    saveBackdatingSettings({ maxBackdateDays })
      .unwrap()
      .then(() => {
        toast.success(intl.formatMessage({ id: "adminConfig.backdating.saveSuccess" }));
      })
      .catch(() => {
        toast.error(intl.formatMessage({ id: "adminConfig.backdating.saveError" }));
      });
  };

  const isLoadingCard = isInitialLoad || (isFetching && !settings);

  return (
    <PageWrapper pageTitle={intl.formatMessage({ id: "adminConfig.title" })}>
      <GlobalS.PageContentCard
        title={intl.formatMessage({ id: "adminConfig.backdating.cardTitle" })}
        loading={isLoadingCard}
      >
        <Paragraph type="secondary">
          {intl.formatMessage({ id: "adminConfig.backdating.description" })}
        </Paragraph>
        <Form
          layout="vertical"
          form={form}
          onFinish={handleSubmit}
          requiredMark={false}
          key={currentValue}
          initialValues={{ maxBackdateDays: currentValue }}
        >
          <Form.Item
            name="maxBackdateDays"
            label={intl.formatMessage({ id: "adminConfig.backdating.fieldLabel" })}
            rules={[{ validator: validateBackdateWindow }]}
            extra={intl.formatMessage({ id: "adminConfig.backdating.fieldHelp" })}
          >
            <InputNumber
              min={MIN_BACKDATE_DAYS}
              max={MAX_BACKDATE_DAYS}
              precision={0}
              data-test-id="backdating-days-input"
              style={{ width: 200 }}
            />
          </Form.Item>
          <Space style={{ marginTop: 16 }}>
            <Button
              onClick={handleReset}
              disabled={!hasChanges || isSaving || isLoadingCard}
              data-test-id="backdating-cancel-btn"
            >
              {intl.formatMessage({ id: "general.cancel" })}
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={isSaving}
              disabled={!hasChanges || isSaving || isLoadingCard}
              data-test-id="backdating-save-btn"
            >
              {intl.formatMessage({ id: "general.save" })}
            </Button>
          </Space>
        </Form>
      </GlobalS.PageContentCard>
    </PageWrapper>
  );
};

export default AdminConfigPage;

